/**
 * Tesoreria › Mastrini (07/10/2026): the customer and supplier ledgers of the
 * accountant, one year at a time, from the Excel of the agreed layout (sheets
 * «Soggetti» and «Movimenti», converted from the PDFs or exported by the
 * accountant). Read-only: a year is replaced whole when loaded again.
 */
import { z } from 'zod';
import { parseIsoDate } from './dates.js';
import { fromCents, toCents } from './money.js';
import { normaliseTaxId, nameKey } from './tax-ids.js';
import { idSchema } from './validation.js';
/** Ciclo attivo: customers (and invoices to issue); ciclo passivo: suppliers. */
export const LEDGER_CYCLES = ['attivo', 'passivo'];
export const LEDGER_CYCLE_LABELS = {
    attivo: 'Ciclo attivo (clienti)',
    passivo: 'Ciclo passivo (fornitori)',
};
function textOf(value) {
    if (value === null || value === undefined)
        return null;
    const text = String(value).trim();
    return text === '' ? null : text;
}
function centsOf(value) {
    if (value === null || value === undefined || value === '')
        return 0;
    if (typeof value === 'number')
        return Number.isFinite(value) ? Math.round(value * 100) : null;
    try {
        return toCents(String(value));
    }
    catch {
        return null;
    }
}
/** A date of the file, or null when missing or impossible (a misread of the PDF, e.g. 2057-25-56). */
function dateOf(value) {
    const text = textOf(value);
    if (!text)
        return null;
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (!iso)
        return null;
    const date = `${iso[1]}-${iso[2]}-${iso[3]}`;
    try {
        parseIsoDate(date);
        return date;
    }
    catch {
        return null;
    }
}
function flagOf(value) {
    if (typeof value === 'boolean')
        return value;
    const text = textOf(value)?.toLowerCase();
    return text === 'true' || text === 'vero' || text === '1' || text === 'si' || text === 'sì';
}
const accountKey = (code, partitario) => `${code}|${partitario ?? ''}`;
/**
 * The year of ledgers from the rows of the two sheets. Every account must
 * add up (opening + dare − avere = closing) and every account of the
 * movements must exist among the subjects: what does not is a problem, and
 * a year with problems is not loaded.
 */
export function readLedger(subjects, movements) {
    const problems = [];
    const years = new Set();
    const accounts = new Map();
    subjects.forEach((row, index) => {
        const line = `Soggetti, riga ${index + 2}`;
        const year = Number(row.anno);
        const cycle = textOf(row.ciclo);
        const accountCode = textOf(row.conto);
        if (!Number.isInteger(year) || year < 2000 || year > 2100)
            return problems.push(`${line}: anno`);
        if (!cycle || !LEDGER_CYCLES.includes(cycle))
            return problems.push(`${line}: ciclo`);
        if (!accountCode)
            return problems.push(`${line}: conto`);
        const amounts = ['saldo_iniziale', 'dare', 'avere', 'saldo_finale'].map((key) => centsOf(row[key]));
        if (amounts.some((amount) => amount === null))
            return problems.push(`${line}: importi`);
        const [opening, debit, credit, closing] = amounts;
        if (opening + debit - credit !== closing) {
            problems.push(`${line}: saldo iniziale + dare − avere non dà il saldo finale`);
        }
        years.add(year);
        const partitario = textOf(row.partitario);
        const key = accountKey(accountCode, partitario);
        if (accounts.has(key))
            return problems.push(`${line}: conto e partitario ripetuti`);
        accounts.set(key, {
            cycle,
            accountCode,
            accountName: textOf(row.conto_descrizione) ?? accountCode,
            partitario,
            name: (textOf(row.denominazione) ?? textOf(row.conto_descrizione) ?? accountCode).slice(0, 200),
            vatNumber: textOf(row.partita_iva),
            taxCode: textOf(row.codice_fiscale),
            foreignId: textOf(row.id_fiscale_estero),
            opening: fromCents(opening),
            debit: fromCents(debit),
            credit: fromCents(credit),
            closing: fromCents(closing),
            entries: [],
        });
    });
    movements.forEach((row, index) => {
        const line = `Movimenti, riga ${index + 2}`;
        const accountCode = textOf(row.conto);
        const account = accountCode
            ? accounts.get(accountKey(accountCode, textOf(row.partitario)))
            : null;
        if (!account)
            return problems.push(`${line}: conto o partitario non presente tra i Soggetti`);
        const [debit, credit, balance] = ['dare', 'avere', 'saldo_progressivo'].map((key) => centsOf(row[key]));
        if (debit == null || credit == null || balance == null) {
            return problems.push(`${line}: importi`);
        }
        account.entries.push({
            registrationDate: dateOf(row.data_registrazione),
            registrationNumber: textOf(row.numero_registrazione)?.slice(0, 40) ?? null,
            causal: textOf(row.causale)?.slice(0, 200) ?? null,
            documentNumber: textOf(row.documento_numero)?.slice(0, 60) ?? null,
            documentDate: dateOf(row.documento_data),
            description: textOf(row.descrizione)?.slice(0, 2000) ?? null,
            debit: fromCents(debit),
            credit: fromCents(credit),
            balance: fromCents(balance),
            counterpart: textOf(row.contropartita)?.slice(0, 300) ?? null,
            opening: flagOf(row.apertura),
        });
    });
    if (years.size > 1)
        problems.push('Il file contiene più anni: un file per anno');
    if (years.size === 0 && problems.length === 0)
        problems.push('Il foglio Soggetti è vuoto');
    return { year: [...years][0] ?? 0, accounts: [...accounts.values()], problems };
}
/**
 * The keys a subject of the ledger is recognised by, the strongest first: VAT
 * number, tax code, foreign id, then the name. A manual match is kept on the
 * first key, so it holds for the other years too.
 */
export function ledgerSubjectKeys(account) {
    const tax = (value) => value ? normaliseTaxId(value).replace(/^IT(?=\d{11}$)/, '') : '';
    return [
        tax(account.vatNumber) && `piva:${tax(account.vatNumber)}`,
        tax(account.taxCode) && `cf:${tax(account.taxCode)}`,
        tax(account.foreignId) && `estero:${tax(account.foreignId)}`,
        nameKey(account.name) && `nome:${nameKey(account.name)}`,
    ].filter((key) => Boolean(key));
}
/** The balance in the normal direction of the cycle: a customer's credit, a supplier's debt. */
export function ledgerBalance(cycle, signed) {
    return cycle === 'attivo' ? signed : fromCents(-toCents(signed));
}
export const importLedgerSchema = z.object({
    /** The Excel, in base64. */
    file: z.string().min(1).max(16_000_000),
    fileName: z.string().trim().min(1).max(200),
    dryRun: z.boolean().default(false),
});
export const ledgerLinkSchema = z.object({
    key: z.string().trim().min(3).max(300),
    /** Null removes the manual match. */
    subjectId: idSchema.nullable(),
});
/**
 * Situazione iniziale (09/10/2026): the invoices still open at 1 January of a
 * year, rebuilt from the lines of the year before. The balance is the
 * opening of the year in the ledger; the invoices are a reconstruction:
 * every reduction (payment, credit note) settles first the invoice it cites
 * by number, then one of the same amount registered with it (self-billing
 * pairs), then one of the same amount, then the oldest. What was open at the
 * start of the year before comes without detail.
 */
export const OPENING_STATES = ['ricostruito', 'da-controllare', 'senza-dettaglio'];
export const OPENING_STATE_LABELS = {
    ricostruito: 'Ricostruito',
    'da-controllare': 'Da controllare',
    'senza-dettaglio': 'Senza dettaglio',
};
/** A document number for comparisons: capitals, no spaces, no leading zeros, no year after it. */
export function documentKey(value) {
    if (!value)
        return '';
    return value
        .toUpperCase()
        .replace(/\s+/g, '')
        .replace(/[/-](?:20)?\d{2}$/, '')
        .replace(/^0+(?=\w)/, '');
}
/** The year written in a document number, «47/2023» or «47-23» → 2023, or null. */
export function documentYear(value) {
    const match = value ? /[/-]((?:20)?\d{2})\s*$/.exec(value.trim()) : null;
    if (!match)
        return null;
    const year = Number(match[1]);
    return year < 100 ? 2000 + year : year;
}
/**
 * Whether a reduction cites an invoice: by its document number, or by «Ft <n>» / «N. <n>» in the text.
 * The number alone is not enough when the years differ (19/10/2026: the collection of «47/2022» had
 * gone to the invoice «47/2023»), nor when the document the reduction cites is older than the invoice.
 */
function cites(reduction, number, date = null) {
    const key = documentKey(number);
    if (!key || !number)
        return false;
    const cited = documentYear(reduction.documentNumber);
    const own = documentYear(number) ?? (date ? Number(date.slice(0, 4)) : null);
    if (cited !== null && own !== null && cited !== own)
        return false;
    if (reduction.documentDate && date && reduction.documentDate < date)
        return false;
    if (documentKey(reduction.documentNumber) === key)
        return true;
    const text = ` ${[reduction.causal, reduction.description].filter(Boolean).join(' ')} `
        .toUpperCase()
        .replace(/\s+/g, ' ');
    const shown = number.toUpperCase().replace(/\s+/g, ' ').trim();
    const withoutYear = shown.replace(/[/-](?:20)?\d{2}$/, '');
    return [shown, withoutYear, withoutYear.replace(/^0+(?=\w)/, '')].some((form) => [`FT ${form} `, `FT. ${form} `, `N. ${form} `, `FATTURA ${form} `].some((cited) => text.includes(cited)));
}
const ANTE = {
    kind: 'ante',
    documentNumber: null,
    documentDate: null,
    registrationDate: null,
    description: null,
};
export function rebuildOpening(input) {
    const sign = input.cycle === 'attivo' ? 1 : -1;
    const natural = (entry) => sign * (toCents(entry.debit) - toCents(entry.credit));
    const balance = sign * toCents(input.opening);
    if (!input.previous) {
        return {
            balance: fromCents(balance),
            items: balance === 0
                ? []
                : [
                    {
                        ...ANTE,
                        amount: fromCents(balance),
                        settled: '0.00',
                        open: fromCents(balance),
                        settledOn: null,
                        invoiceId: null,
                        residual: null,
                    },
                ],
            state: balance > 0 ? 'senza-dettaglio' : 'da-controllare',
            difference: null,
        };
    }
    const docs = [];
    const reductions = [];
    const before = sign * toCents(input.previous.opening);
    if (before > 0)
        docs.push({ item: ANTE, entry: null, amount: before, left: before });
    let pool = before < 0 ? -before : 0;
    for (const entry of input.previous.entries) {
        if (entry.opening)
            continue;
        const value = natural(entry);
        if (value > 0) {
            docs.push({
                item: {
                    kind: 'documento',
                    documentNumber: entry.documentNumber,
                    documentDate: entry.documentDate,
                    registrationDate: entry.registrationDate,
                    description: entry.description ?? entry.causal,
                },
                entry,
                amount: value,
                left: value,
            });
        }
        else if (value < 0)
            reductions.push({ entry, left: -value });
    }
    const settle = (doc, reduction) => {
        const paid = Math.min(doc.left, reduction.left);
        doc.left -= paid;
        reduction.left -= paid;
    };
    // 1. The invoice the reduction cites, the oldest first.
    for (const reduction of reductions) {
        for (const doc of docs) {
            if (reduction.left === 0)
                break;
            if (doc.left > 0 &&
                doc.entry &&
                cites(reduction.entry, doc.item.documentNumber, doc.item.documentDate)) {
                settle(doc, reduction);
            }
        }
    }
    // 2. The same amount registered together (a self-billing and its VAT transfer), 3. the same amount.
    const together = (doc, entry) => doc.entry !== null &&
        ((entry.registrationNumber !== null &&
            doc.entry.registrationNumber === entry.registrationNumber) ||
            (entry.registrationDate !== null && doc.entry.registrationDate === entry.registrationDate));
    for (const strict of [true, false]) {
        for (const reduction of reductions) {
            if (reduction.left === 0)
                continue;
            const doc = docs.find((item) => item.left === reduction.left && (!strict || together(item, reduction.entry)));
            if (doc)
                settle(doc, reduction);
        }
    }
    // 4. The oldest first.
    pool += reductions.reduce((sum, reduction) => sum + reduction.left, 0);
    for (const doc of docs) {
        if (pool === 0)
            break;
        const paid = Math.min(doc.left, pool);
        doc.left -= paid;
        pool -= paid;
    }
    const payments = input.following.filter((entry) => !entry.opening && natural(entry) < 0);
    const items = docs
        .filter((doc) => doc.left > 0)
        .map((doc) => {
        const payment = doc.item.kind === 'documento'
            ? payments.find((entry) => cites(entry, doc.item.documentNumber, doc.item.documentDate))
            : undefined;
        return {
            ...doc.item,
            amount: fromCents(doc.amount),
            settled: fromCents(doc.amount - doc.left),
            open: fromCents(doc.left),
            settledOn: payment?.registrationDate ?? null,
            invoiceId: null,
            residual: null,
        };
    });
    const difference = balance - items.reduce((sum, item) => sum + toCents(item.open), 0);
    return {
        balance: fromCents(balance),
        items,
        state: difference === 0 && balance >= 0 ? 'ricostruito' : 'da-controllare',
        difference: difference === 0 ? null : fromCents(difference),
    };
}
/**
 * What ties an item of the fixed situation to money (2.13): undone when the
 * situation moves to another 1 January, since its items go.
 */
export const OPENING_MOVE_LINK_KINDS = [
    'banca',
    'carta',
    'nota',
    'compensazione',
    'chiusura',
    'a-mano',
    'anticipo',
];
export const OPENING_MOVE_LINK_LABELS = {
    banca: 'Riconciliazione del movimento bancario',
    carta: 'Rendicontazione della spesa con carta',
    nota: 'Collegamento con la nota di credito',
    compensazione: 'Compensazione',
    chiusura: 'Chiusura senza movimento o abbuono',
    'a-mano': 'Incasso o pagamento segnato a mano',
    anticipo: 'Anticipo della banca sulla partita',
};
export const openingMoveSchema = z.object({
    to: z.number().int().min(2000).max(2100),
});
//# sourceMappingURL=ledgers.js.map